import {
  IconDefinition,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Collapse } from '.';
import { documentationContents } from '../content/documentation-contents';
import {
  DocumentationContents,
  DocumentationContentsHeading,
  DocumentationContentsPage,
  DocumentationContentsSection,
} from '../types';
import classNames from '../utilities/class-names';
import {
  documentationContentsSectionKey,
  findDocumentationContentsSectionKeys,
  isDocumentationContentsHeading,
  isDocumentationContentsPage,
  isDocumentationContentsSection,
  isDocumentationContentsSeparator,
} from '../utilities/documentation-contents';
import './DocumentationContentsMenu.scss';

type DocumentationContentsMenuSectionProps = {
  section: DocumentationContentsSection;
  expanded: boolean;
};

function DocumentationContentsMenuSection({
  section,
  expanded,
}: DocumentationContentsMenuSectionProps): ReactElement {
  const location = useLocation();

  return (
    <>
      <a
        className={classNames(
          'documentation-contents-item-section',
          location.pathname === `/docs${section.url}`
            ? 'documentation-contents-item-active'
            : ''
        )}
        href={`/docs${section.url}`}
      >
        {section.icon && (
          <FontAwesomeIcon
            className="documentation-contents-icon"
            icon={section.icon as unknown as IconDefinition}
          />
        )}
        {section.title}
      </a>
      {/* Outside the link, so it hints that the rest of the row toggles the
      section rather than navigating */}
      <FontAwesomeIcon
        className={classNames(
          'documentation-contents-section-caret',
          expanded ? 'documentation-contents-section-caret-expanded' : undefined
        )}
        icon={faChevronDown}
      />
    </>
  );
}

type DocumentationContentsMenuPageProps = {
  page: DocumentationContentsPage;
};

function DocumentationContentsMenuPage({
  page,
}: DocumentationContentsMenuPageProps): ReactElement {
  const location = useLocation();

  return (
    <a
      className={classNames(
        'documentation-contents-item-page',
        location.pathname === `/docs${page.url}`
          ? 'documentation-contents-item-active'
          : ''
      )}
      href={`/docs${page.url}`}
    >
      {page.icon && (
        <FontAwesomeIcon
          className="documentation-contents-icon"
          icon={page.icon as unknown as IconDefinition}
        />
      )}
      {page.title}
    </a>
  );
}

type DocumentationContentsMenuHeadingProps = {
  heading: DocumentationContentsHeading;
};

function DocumentationContentsMenuHeading({
  heading,
}: DocumentationContentsMenuHeadingProps): ReactElement {
  return <h2 className="documentation-contents-heading">{heading.title}</h2>;
}

function DocumentationContentsMenuSeparator(): ReactElement {
  return <hr className="documentation-contents-separator" />;
}

export function DocumentationContentsMenu(): ReactElement {
  const location = useLocation();

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(findDocumentationContentsSectionKeys(location.pathname))
  );

  // Expand the sections containing the current page, but otherwise leave the
  // menu as the user left it
  useEffect(() => {
    const activeSections = findDocumentationContentsSectionKeys(
      location.pathname
    );

    setExpandedSections(current =>
      activeSections.every(key => current.has(key))
        ? current
        : new Set([...Array.from(current), ...activeSections])
    );
  }, [location.pathname]);

  const handleSectionHeaderClick = useCallback(
    (key: string, event: MouseEvent<HTMLDivElement>) => {
      // Clicking the section's own link navigates to it (or opens it in a new
      // tab), so it should expand the section rather than toggle it
      const clickedLink = !!(event.target as Element).closest('a');
      if (
        clickedLink &&
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      setExpandedSections(current => {
        const next = new Set(current);
        if (clickedLink || !current.has(key)) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    []
  );

  const buildMenu = (
    contents: DocumentationContents,
    parentKey: string = ''
  ): ReactElement => {
    return (
      <ul>
        {contents.map((item, index) => {
          const key = documentationContentsSectionKey(parentKey, index);

          return (
            <li key={key}>
              {isDocumentationContentsSection(item) && (
                <Collapse
                  className="documentation-contents-section-collapse"
                  background={false}
                  closable={false}
                  expanded={expandedSections.has(key)}
                  onHeaderClick={(event: MouseEvent<HTMLDivElement>) =>
                    handleSectionHeaderClick(key, event)
                  }
                  header={
                    <DocumentationContentsMenuSection
                      section={item}
                      expanded={expandedSections.has(key)}
                    />
                  }
                >
                  {buildMenu(item.contents, key)}
                </Collapse>
              )}
              {isDocumentationContentsPage(item) && (
                <DocumentationContentsMenuPage page={item} />
              )}
              {isDocumentationContentsHeading(item) && (
                <DocumentationContentsMenuHeading heading={item} />
              )}
              {isDocumentationContentsSeparator(item) && (
                <DocumentationContentsMenuSeparator />
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <nav className="documentation-contents-menu">
      <div className="documentation-contents-menu-inner">
        {buildMenu(documentationContents)}
      </div>
    </nav>
  );
}
